/**
 * Mobile-internal batch selection provider for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal copy of desktop BatchSelectionProvider.
 * Context value shape matches desktop 1:1 (selectedPlayers Set + helpers).
 *
 * Behavioral guarantees (preserved from desktop):
 *   - togglePlayerSelection: adds ONLY if player is unowned + has ids.league_player.
 *     Remove always works regardless of ownership.
 *   - selectAllPlayers: filters out owned players + players missing ids.league_player.
 *   - toggleBulkMode: disabling (true→false) clears all selections.
 *
 * Consumers (mobile-internal only):
 *   - MobileBulkAddBanner
 *   - MobilePlayerTable
 *   - MobileSearchControls
 *   - MobileFreeAgentSearch (provider wrapper)
 */

import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

const MobileBatchSelectionContext = createContext(null);

export function useMobileBatchSelection() {
  const context = useContext(MobileBatchSelectionContext);
  if (!context) {
    throw new Error('useMobileBatchSelection must be used within MobileBatchSelectionProvider');
  }
  return context;
}

export function MobileBatchSelectionProvider({ children }) {
  const [selectedPlayers, setSelectedPlayers] = useState(() => new Set());
  const [bulkMode, setBulkMode] = useState(false);

  // Disabling bulk mode clears selections (preserves desktop contract)
  const toggleBulkMode = useCallback(() => {
    setBulkMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedPlayers(new Set());
      }
      return next;
    });
  }, []);

  const togglePlayerSelection = useCallback((player) => {
    setSelectedPlayers((prev) => {
      const newSet = new Set(prev);
      const playerId = player?.ids?.league_player;
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else if (!player?.league_context?.team?.team_name && playerId) {
        // Only allow selection if player is available (not owned) AND has an id
        newSet.add(playerId);
      }
      return newSet;
    });
  }, []);

  const isPlayerSelected = useCallback(
    (player) => selectedPlayers.has(player?.ids?.league_player),
    [selectedPlayers],
  );

  const selectAllPlayers = useCallback((players) => {
    const availablePlayers = (players || []).filter(
      (p) => !p?.league_context?.team?.team_name && p?.ids?.league_player,
    );
    const newSet = new Set();
    availablePlayers.forEach((p) => newSet.add(p.ids.league_player));
    setSelectedPlayers(newSet);
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelectedPlayers(new Set());
  }, []);

  const getSelectedPlayerObjects = useCallback(
    (players) => (players || []).filter((p) => selectedPlayers.has(p?.ids?.league_player)),
    [selectedPlayers],
  );

  const selectionStats = useMemo(
    () => ({
      count: selectedPlayers.size,
      hasSelections: selectedPlayers.size > 0,
    }),
    [selectedPlayers],
  );

  const value = useMemo(
    () => ({
      selectedPlayers,
      selectionStats,
      bulkMode,
      toggleBulkMode,
      togglePlayerSelection,
      isPlayerSelected,
      selectAllPlayers,
      clearAllSelections,
      getSelectedPlayerObjects,
    }),
    [
      selectedPlayers,
      selectionStats,
      bulkMode,
      toggleBulkMode,
      togglePlayerSelection,
      isPlayerSelected,
      selectAllPlayers,
      clearAllSelections,
      getSelectedPlayerObjects,
    ],
  );

  return (
    <MobileBatchSelectionContext.Provider value={value}>
      {children}
    </MobileBatchSelectionContext.Provider>
  );
}

export default MobileBatchSelectionProvider;
